import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ChatSideBar from "./ChatSideBar";
import ChatWindow from "./ChatWindow";
import axios from "axios";
import Cookies from "js-cookie";
import { useSelector } from "react-redux";
import PopUp from "Pages/Chatbot/PopUp";
import toast from "react-hot-toast";
import ChatTopBar from "ThemeLayout/ChatTopBar";

const ChatPage = () => {
  const { tabId } = useParams();
  const navigate = useNavigate();
  const [selectedMode, setSelectedMode] = useState("Legal ChatBot");
  const [tabs, setTabs] = useState([]);
  const [tabsLoading, setTabsLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [isSidebarVisible, setSidebarVisible] = useState(false);
  const token = Cookies.get("token");
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const [loginPopup, setLoginPopup] = useState(false);
  const isAuthenticated = useSelector((state) => state.theme.isAuthenticated);

  // Fetch all tabs based on the selected mode
  const fetchTabs = async () => {
    try {
      setTabsLoading(true);
      const endpoint =
        selectedMode === "Legal ChatBot"
          ? `${baseUrl}conversation/all-tabs`
          : `${baseUrl}document/all-tabs`;
      const res = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTabs(res.data.tabs);
      setTabsLoading(false);
    } catch (err) {
      console.error("Error loading tabs", err);
      setTabsLoading(false);
    }
  };

  // Fetch messages for the current tab
  const fetchMessages = async () => {
    if (!tabId) return setMessages([]);
    try {
      const endpoint =
        selectedMode === "Legal ChatBot"
          ? `${baseUrl}conversation/chats/${tabId}`
          : `${baseUrl}document/tabs/${tabId}`;
      const res = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.messages && res.data.messages.length > 0) {
        setMessages(res.data.messages || []);
      } else {
        setMessages(
          [
            { role: "assistant", content: `${res.data.documents[0].content}` },
          ] || []
        );
      }
    } catch (err) {
      console.error("Error loading messages", err);
    }
  };

  // Handle sending messages for both chat and document tabs
  const handleSendMessage = async (text) => {
    if (!isAuthenticated) {
      toast.error("Please log in to use this feature.");
      setLoginPopup(true);
      return;
    }
    try {
      if (selectedMode === "Legal ChatBot") {
        const userMessage = { role: "user", content: text };
        setMessages((prev) => [...prev, userMessage]);
      }

      if (!tabId) {
        // Create new tab (for chat or document)
        const createTabEndpoint =
          selectedMode === "Legal ChatBot"
            ? `${baseUrl}conversation/chats`
            : `${baseUrl}document/tabs`;
        const create = await axios.post(
          createTabEndpoint,
          { message: text },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const newTab = create.data.tab;
        setTabs((prev) => [newTab, ...prev]); // Update tab list with new title
        navigate(`/chatbot/${newTab.tabId}`);

        // Send message to the newly created tab
        const sendMessageEndpoint =
          selectedMode === "Legal ChatBot"
            ? `${baseUrl}conversation/chats/${newTab.tabId}/message`
            : `${baseUrl}document/tabs/${newTab.tabId}/generate`;
        const send = await axios.post(
          sendMessageEndpoint,
          { message: text },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const document =
          selectedMode === "Legal ChatBot"
            ? ""
            : [
                {
                  role: "assistant",
                  content: `Your document is ready:\n\n${send.data.document.content}`,
                },
              ];

        console.log("sendMessage", send.data.messages);

        setMessages(
          selectedMode === "Legal ChatBot"
            ? send.data.messages || []
            : document || []
        );
        updateTabTitle(
          newTab.tabId,
          send.data.title || send.data.document.title || "Untitled"
        );
      } else {
        // Send message to existing tab
        const sendMessageEndpoint =
          selectedMode === "Legal ChatBot"
            ? `${baseUrl}conversation/chats/${tabId}/message`
            : `${baseUrl}document/tabs/${tabId}/generate`;

        const send = await axios.post(
          sendMessageEndpoint,
          { message: text },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setMessages(
          selectedMode === "Legal ChatBot"
            ? send.data.messages
            : send.data.document.content
        );
        updateTabTitle(
          tabId,
          send.data.title || send.data.document.title || "Untitled"
        );
      }
    } catch (err) {
      console.error("Send message error", err);
    }
  };

  // Helper function to update tab title dynamically
  const updateTabTitle = (id, newTitle) => {
    setTabs((prevTabs) =>
      prevTabs.map((tab) =>
        tab.tabId === id ? { ...tab, title: newTitle } : tab
      )
    );
  };

  // Fetch all tabs and messages on mode change or authentication change
  useEffect(() => {
    if (!isAuthenticated) return;
    fetchTabs();
    setMessages([]);
  }, [isAuthenticated, selectedMode]);

  // Fetch messages when tabId changes
  useEffect(() => {
    fetchMessages();
  }, [tabId, selectedMode]);

  return (
    <>
      <ChatTopBar
        setSelectedMode={setSelectedMode}
        selectedMode={selectedMode}
      />
      <div className="container-fluid app-layout">
        <div className="row">
          <div className="col-12 col-md-2 p-0 chat-sidebar-container">
            <ChatSideBar
              isLoading={tabsLoading}
              chats={tabs}
              onSelectChat={(id) => {
                navigate(`/chatbot/${id}`);
                setMessages([]);
                setTimeout(() => {
                  setSidebarVisible(false);
                }, 300);
              }}
              newChat={() => navigate(`/chatbot`)}
              isSidebarVisible={isSidebarVisible}
              toggleSidebar={() => setSidebarVisible((prev) => !prev)}
            />
          </div>

          <div className="col-12 col-md-10 p-0">
            <ChatWindow
              messages={messages}
              onSendMessage={handleSendMessage}
              isNewChat={!tabId}
              mode={selectedMode}
            />
          </div>
        </div>
        <button
          className="btn d-md-none sidebar-toggle"
          onClick={() => setSidebarVisible((prev) => !prev)}
        >
          &#9776;
        </button>
        {loginPopup && <PopUp onClose={() => setLoginPopup(false)} />}
      </div>
    </>
  );
};

export default ChatPage;
